import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { CollectionUpdateInput } from "@/modules/collections/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircleIcon } from "lucide-react";
import { useCollectionQuery, useUpdateCollection } from "../api/queries";
import { CollectionFormTour, useCollectionFormTour } from "../tour";
import { CollectionForm } from "../ui/CollectionForm";

export function CollectionEditPage() {
  const nav = useNavigate({ from: "/app/catalog/collections/$id/edit" });
  const { id } = useParams({ from: "/app/catalog/collections/$id/edit" });
  const { data, ...result } = useCollectionQuery(id);
  const updateCollection = useUpdateCollection(id);
  const { run, handleJoyrideCallback, startTour } = useCollectionFormTour();

  const collection = data;
  const collectionName =
    collection &&
    typeof collection.name === "object" &&
    collection.name !== null
      ? ((collection.name as { lo?: string })?.lo ??
        (collection.name as { en?: string })?.en ??
        collection.slug)
      : (collection?.slug ?? id ?? "ແກ້ໄຂ");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄໍເລັກຊັນ", to: "/app/catalog/collections" },
            { label: collectionName },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂ Collection{" "}
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="ml-2 size-6"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircleIcon className="h-4 w-4" />
              </Button>
            </h2>
            <p className="text-muted-foreground">ປັບລາຍລະອຽດຂອງ Collection.</p>
          </div>

          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/catalog/collections" } as any)}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດ Collection"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!collection ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <CollectionForm
                initialValues={{
                  nameLo: collection.name.lo ?? "",
                  nameTh: collection.name.th ?? "",
                  nameEn: collection.name.en ?? "",
                  slug: collection.slug,
                  sortOrder: collection.sortOrder,
                }}
                onSubmit={async (values: CollectionUpdateInput) => {
                  try {
                    await updateCollection.mutateAsync(values);
                    nav({ to: "/app/catalog/collections" } as any);
                  } catch (error) {
                    // Error handling is done by mutation
                  }
                }}
                submitting={updateCollection.isPending}
              />
            </div>
          )}
        </QueryState>
        <CollectionFormTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
